import { MutableRefObject, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { FieldError } from '../../../../components/novarium/NvTableParts';
import useNotify from '../../../../hooks/useNotify';
import { parseApiError } from '../../../../services/apiError';
import { getMessageOptions, sendForms } from '../../services/AgendaService';
import { BillingDocument, MessageOptions } from '../../types/billing.types';
import { errorMessageOf, fieldErrorsOf, FieldErrors, openFormPdf } from './billingUtils';

interface DocumentsStepProps {
  patientId: number;
  documents: BillingDocument[];
  exitRef?: MutableRefObject<(() => void) | null>;
  onClose: () => void;
}

/**
 * Paso "Comprobantes": el PDF de la factura y del recibo, y el envío de las facturas al paciente por WhatsApp o mail
 * (como la ventana de pago de Vaadin, se envían las facturas, no el recibo).
 */
export default function DocumentsStep({ patientId, documents, exitRef, onClose }: DocumentsStepProps) {
  const notify = useNotify();
  const [messageOptions, setMessageOptions] = useState<MessageOptions | null>(null);
  const [channelId, setChannelId] = useState<number | null>(null);
  const [to, setTo] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState<number | null>(null);

  const invoices = documents.filter((d) => d.kind === 'invoice');

  useEffect(() => {
    if (exitRef) exitRef.current = onClose;
  }, [exitRef, onClose]);

  useEffect(() => {
    let cancelled = false;
    getMessageOptions(patientId)
      .then((data) => {
        if (cancelled) return;
        setMessageOptions(data);
        // Como el panel de envío de Vaadin: si el paciente tiene mail y el sitio envía por mail, va preseleccionado
        const initial = data.email && data.emailChannelId ? data.emailChannelId : data.channels[0]?.id ?? null;
        setChannelId(initial);
        setTo(contactFor(data, initial));
      })
      .catch((error: unknown) => {
        parseApiError(error);
        if (!cancelled) setMessageOptions({ channels: [], emailChannelId: null, whatsappChannelId: null, email: null, mobilePhone: null });
      });
    return () => { cancelled = true; };
  }, [patientId]);

  function contactFor(data: MessageOptions, id: number | null): string {
    if (id != null && id === data.emailChannelId) return data.email ?? '';
    if (id != null && id === data.whatsappChannelId) return data.mobilePhone ?? '';
    return '';
  }

  async function handleOpen(document: BillingDocument) {
    setOpening(document.adminFormId);
    try {
      await openFormPdf(document.adminFormId);
    } catch (error: unknown) {
      notify.error(errorMessageOf(parseApiError(error), 'No se pudo abrir el PDF. Intentá de nuevo.'));
    } finally {
      setOpening(null);
    }
  }

  async function handleSend() {
    const validation: FieldErrors = {};
    if (!channelId) validation.messageTypeId = 'Elegí por dónde enviarlo';
    if (!to.trim()) validation.to = channelId === messageOptions?.whatsappChannelId ? 'Completá el celular' : 'Completá el mail';
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    setSending(true);
    try {
      await sendForms({ patientId, adminFormIds: invoices.map((d) => d.adminFormId), messageTypeId: channelId!, to: to.trim() });
      notify.success(invoices.length > 1 ? 'Facturas enviadas' : 'Factura enviada');
    } catch (error: unknown) {
      const apiError = parseApiError(error);
      const fieldErrors = fieldErrorsOf(apiError);
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else notify.error(errorMessageOf(apiError, 'No se pudo enviar la factura. Intentá de nuevo.'));
    } finally {
      setSending(false);
    }
  }

  const isWhatsapp = channelId != null && channelId === messageOptions?.whatsappChannelId;

  return (
    <>
      <section className="nv-section" aria-labelledby="documents-title">
        <h2 id="documents-title" className="nv-section-title">Comprobantes</h2>
        <ul className="nv-stack-4">
          {documents.map((document) => (
            <li key={document.adminFormId} className="nv-row-8">
              <span className="nv-row-8">
                {document.label}
                {document.pendingAfip && <Tag className="nv-tag-warn" value="Pendiente de AFIP" />}
              </span>
              <Button
                type="button"
                className="buttonMain3"
                icon="pi pi-file-pdf"
                label="Ver PDF"
                onClick={() => handleOpen(document)}
                loading={opening === document.adminFormId}
                disabled={document.pendingAfip}
                tooltip={document.pendingAfip ? 'Se puede imprimir cuando AFIP la autorice' : undefined}
                tooltipOptions={{ showOnDisabled: true, event: 'both' }}
              />
            </li>
          ))}
        </ul>
      </section>

      {invoices.length > 0 && (
        <section className="nv-section" aria-labelledby="documents-send-title">
          <h2 id="documents-send-title" className="nv-section-title">Enviar al paciente</h2>
          {messageOptions && messageOptions.channels.length === 0 ? (
            <p className="nv-note"><i className="pi pi-info-circle" aria-hidden /> El sitio no tiene habilitado el envío de facturas.</p>
          ) : (
            <>
              <p className="nv-note"><i className="pi pi-info-circle" aria-hidden /> Se envían las facturas, no el recibo.</p>
              <div className="nv-form-row">
                <div className="nv-field nv-w-s">
                  <label htmlFor="documents-channel">Mensajería</label>
                  <Dropdown panelClassName="nv-scope"
                    inputId="documents-channel"
                    value={channelId}
                    options={messageOptions?.channels ?? []}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Seleccionar"
                    onChange={(e) => {
                      setChannelId(e.value ?? null);
                      if (messageOptions) setTo(contactFor(messageOptions, e.value ?? null));
                      setErrors({});
                    }}
                    className={errors.messageTypeId ? 'p-invalid' : undefined}
                  />
                  <FieldError id="documents-channel-error" message={errors.messageTypeId} />
                </div>
                <div className="nv-field nv-w-m">
                  <label htmlFor="documents-to">{isWhatsapp ? 'Celular' : 'Mail'}</label>
                  <InputText
                    id="documents-to"
                    value={to}
                    maxLength={255}
                    placeholder={isWhatsapp ? 'Ej.: +542236236647' : 'Ej.: nombre@dominio.com'}
                    onChange={(e) => { setTo(e.target.value); setErrors((c) => ({ ...c, to: '' })); }}
                    className={errors.to ? 'p-invalid' : undefined}
                    aria-invalid={Boolean(errors.to)}
                    aria-describedby={errors.to ? 'documents-to-error' : undefined}
                  />
                  <FieldError id="documents-to-error" message={errors.to || undefined} />
                </div>
                <div className="nv-field-inline-action">
                  <Button type="button" className="buttonMain2" outlined icon="pi pi-send" label="Enviar" onClick={handleSend} loading={sending} />
                </div>
              </div>
            </>
          )}
        </section>
      )}

      <footer className="nv-page-foot">
        <hr className="nv-divider" />
        <div className="nv-actions-end">
          <Button type="button" className="buttonMain" label="Listo" onClick={onClose} />
        </div>
      </footer>
    </>
  );
}
